import { useEffect, useRef, useState, type DragEvent } from "react";
import type {
  LeadStatus,
  WorkflowPipelineItem,
  WorkflowPipelineResource,
  WorkflowPipelineStage,
} from "@crewfsm/contracts";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@crewfsm/ui/app";
import { ArrowRightLeft } from "lucide-react";
import { canMoveLead, type PipelineStatus } from "./pipeline-model";
import type { PipelineApi } from "./pipeline-api";
import type { OpenWorkflowAction } from "./workflow-actions";
import { WORKFLOW_LABELS } from "./workflow-model";

type MoveEffect =
  | { kind: "STATUS"; status: PipelineStatus }
  | { kind: "ACTION"; action: "HOLD" | "RESUME" }
  | { kind: "DETAIL"; resource: WorkflowPipelineResource; instruction: string };
export type WorkflowMove = {
  stage: WorkflowPipelineStage;
  hint: string;
} & MoveEffect;

// These are UI affordances for existing commands; each module still validates
// its own transitions. A projected stage is never written directly.
export function workflowMoves(
  item: WorkflowPipelineItem,
  permissions: readonly string[],
): WorkflowMove[] {
  if (item.stage === "ON_HOLD")
    return item.quickActions.includes("RESUME")
      ? [
          {
            stage: item.underlyingStage,
            kind: "ACTION",
            action: "RESUME",
            hint: "Resume work…",
          },
        ]
      : [];
  const moves: WorkflowMove[] = [];
  if (item.resource.type === "LEAD" && permissions.includes("leads:write")) {
    const targets = {
      NEW_REQUEST: "OPEN",
      CONTACTED: "CONTACTED",
      ESTIMATE_NEEDED: "QUALIFIED",
      CLOSED: "LOST",
    } as const;
    for (const [stage, status] of Object.entries(targets)) {
      if (
        stage !== item.stage &&
        canMoveLead(item.status as LeadStatus, status)
      )
        moves.push({
          stage: stage as WorkflowPipelineStage,
          kind: "STATUS",
          status,
          hint: status === "LOST" ? "Mark lead lost" : "Move lead",
        });
    }
  }
  const continuation: Partial<
    Record<WorkflowPipelineStage, [WorkflowPipelineStage, string]>
  > =
    item.resource.type === "ESTIMATE"
      ? {
          ESTIMATE_NEEDED: [
            "AWAITING_CUSTOMER",
            "Send the estimate from its detail page.",
          ],
          AWAITING_CUSTOMER: [
            "APPROVED",
            "Review and record customer approval on the estimate.",
          ],
          APPROVED: [
            "READY_TO_SCHEDULE",
            "Convert the accepted estimate to a job from its detail page.",
          ],
        }
      : item.resource.type === "JOB"
        ? {
            READY_TO_SCHEDULE: [
              "SCHEDULED",
              "Schedule an appointment from the job detail page.",
            ],
            SCHEDULED: ["IN_PROGRESS", "Start work from the job detail page."],
            IN_PROGRESS: [
              "TO_INVOICE",
              "Complete work from the job detail page.",
            ],
            TO_INVOICE: [
              "AWAITING_PAYMENT",
              "Create and send an invoice from the job detail page.",
            ],
          }
        : {};
  const next = continuation[item.stage];
  const readPermission =
    item.resource.type === "ESTIMATE" ? "estimates:read" : "jobs:read";
  if (next && permissions.includes(readPermission))
    moves.push({
      stage: next[0],
      kind: "DETAIL",
      resource: item.resource,
      instruction: next[1],
      hint: `Continue in ${item.resource.type.toLowerCase()}…`,
    });
  if (
    item.stage === "AWAITING_PAYMENT" &&
    permissions.includes("invoices:read")
  ) {
    const invoice =
      item.resource.type === "INVOICE"
        ? item.resource
        : item.resources.find((resource) => resource.type === "INVOICE");
    if (invoice)
      moves.push({
        stage: "PAID",
        kind: "DETAIL",
        resource: invoice,
        instruction:
          "Collect or record the payment from the invoice detail page.",
        hint: "Continue in invoice…",
      });
  }
  if (item.quickActions.includes("HOLD"))
    moves.push({
      stage: "ON_HOLD",
      kind: "ACTION",
      action: "HOLD",
      hint: "Choose a hold reason…",
    });
  return moves;
}

export function WorkflowMoveMenu({
  item,
  moves,
  disabled,
  onMove,
}: {
  item: WorkflowPipelineItem;
  moves: WorkflowMove[];
  disabled: boolean;
  onMove: (
    item: WorkflowPipelineItem,
    stage: WorkflowPipelineStage,
    trigger: HTMLButtonElement | null,
  ) => void;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const openingPanel = useRef(false);
  return (
    <DropdownMenu
      modal={false}
      onOpenChange={(open) => {
        if (open) openingPanel.current = false;
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          ref={trigger}
          variant="ghost"
          size="icon-sm"
          className="workflow-move-trigger size-10 shrink-0 md:size-8"
          disabled={disabled}
          aria-label={`Move ${item.title}`}
          title="Move to another stage"
          data-no-drag
        >
          <ArrowRightLeft />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="workflow-theme min-w-52"
        onCloseAutoFocus={(event) => {
          if (openingPanel.current) event.preventDefault();
        }}
      >
        <DropdownMenuLabel>Move to</DropdownMenuLabel>
        {moves.map((move) => (
          <DropdownMenuItem
            key={move.stage}
            disabled={disabled}
            className="min-h-10 flex-col items-start gap-0.5"
            onSelect={() => {
              openingPanel.current = move.kind === "ACTION";
              onMove(item, move.stage, trigger.current);
            }}
          >
            <span>{WORKFLOW_LABELS[move.stage]}</span>
            {move.kind !== "STATUS" || move.status === "LOST" ? (
              <span className="text-xs text-muted-foreground">{move.hint}</span>
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function useWorkflowMovement({
  scope,
  api,
  permissions,
  blocked,
  onAction,
  onSaved,
  onError,
}: {
  scope: string;
  api: PipelineApi;
  permissions: readonly string[];
  blocked: boolean;
  onAction: OpenWorkflowAction;
  onSaved: (message: string, stage: WorkflowPipelineStage) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [dragged, setDragged] = useState<WorkflowPipelineItem | null>(null);
  const [over, setOver] = useState<WorkflowPipelineStage | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [continuation, setContinuation] = useState<
    (Extract<WorkflowMove, { kind: "DETAIL" }> & { title: string }) | null
  >(null);
  const active = useRef<WorkflowPipelineItem | null>(null);
  const saving = useRef(false);
  const epoch = useRef(0);
  const scrollFrame = useRef<number | null>(null);
  const suppressClickUntil = useRef(0);
  const blockedOrigin = useRef(false);
  const pointerHeld = useRef(false);
  const stopScroll = () => {
    if (scrollFrame.current !== null) cancelAnimationFrame(scrollFrame.current);
    scrollFrame.current = null;
  };
  const cancel = () => {
    if (active.current) suppressClickUntil.current = Date.now() + 250;
    active.current = null;
    pointerHeld.current = false;
    setDragged(null);
    setOver(null);
    stopScroll();
  };
  useEffect(() => {
    epoch.current++;
    active.current = null;
    pointerHeld.current = false;
    setDragged(null);
    setOver(null);
    setContinuation(null);
    return () => {
      epoch.current++;
      if (scrollFrame.current !== null)
        cancelAnimationFrame(scrollFrame.current);
      scrollFrame.current = null;
    };
  }, [scope, api]);
  useEffect(() => {
    const release = () => {
      pointerHeld.current = false;
    };
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    window.addEventListener("blur", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
      window.removeEventListener("blur", release);
    };
  }, []);
  useEffect(() => {
    if (!dragged) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") cancel();
    };
    const leaveWindow = () => cancel();
    window.addEventListener("keydown", escape);
    window.addEventListener("blur", leaveWindow);
    return () => {
      window.removeEventListener("keydown", escape);
      window.removeEventListener("blur", leaveWindow);
    };
  }, [dragged]);

  const move = async (
    item: WorkflowPipelineItem,
    stage: WorkflowPipelineStage,
    trigger: HTMLButtonElement | null,
  ) => {
    if (blocked || saving.current) return;
    const destination = workflowMoves(item, permissions).find(
      (value) => value.stage === stage,
    );
    if (!destination) return;
    cancel();
    setContinuation(null);
    if (destination.kind === "ACTION") {
      onAction(item, destination.action, trigger);
      return;
    }
    if (destination.kind === "DETAIL") {
      setContinuation({ ...destination, title: item.title });
      return;
    }
    const current = epoch.current;
    saving.current = true;
    setMovingId(item.id);
    try {
      const lead = await api.lead(item.resource.id);
      if (current !== epoch.current) return;
      if (
        lead.status !== item.status ||
        !canMoveLead(lead.status, destination.status)
      )
        throw new Error(
          "This lead changed. Refresh the pipeline before moving it again.",
        );
      await api.updateStatus(lead.id, destination.status);
      if (current === epoch.current)
        await onSaved(
          `${item.title} moved to ${WORKFLOW_LABELS[stage]}.`,
          stage,
        );
    } catch (cause) {
      if (current === epoch.current)
        onError(
          cause instanceof Error
            ? cause.message
            : "The lead could not move. Refresh and try again.",
        );
    } finally {
      saving.current = false;
      setMovingId(null);
    }
  };
  const dragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!active.current) return;
    const board = event.currentTarget;
    const bounds = board.getBoundingClientRect();
    const direction =
      event.clientX < bounds.left + 48
        ? -1
        : event.clientX > bounds.right - 48
          ? 1
          : 0;
    stopScroll();
    if (direction) {
      const tick = () => {
        board.scrollLeft += direction * 9;
        scrollFrame.current = requestAnimationFrame(tick);
      };
      scrollFrame.current = requestAnimationFrame(tick);
    }
  };
  return {
    dragged,
    over,
    movingId,
    continuation,
    cancel,
    dismissContinuation: () => setContinuation(null),
    interacting: () =>
      Boolean(active.current || saving.current || pointerHeld.current),
    move: (
      item: WorkflowPipelineItem,
      stage: WorkflowPipelineStage,
      trigger: HTMLButtonElement | null,
    ) => {
      void move(item, stage, trigger);
    },
    boardProps: {
      onDragOver: dragOver,
      onDragLeave: (event: DragEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          stopScroll();
          setOver(null);
        }
      },
    },
    cardProps: (item: WorkflowPipelineItem) => ({
      draggable:
        !blocked && !movingId && workflowMoves(item, permissions).length > 0,
      "data-dragging": dragged?.id === item.id,
      "aria-busy": movingId === item.id,
      onPointerDownCapture: (event: React.PointerEvent<HTMLDivElement>) => {
        blockedOrigin.current = Boolean(
          (event.target as Element).closest("[data-no-drag]"),
        );
        pointerHeld.current = !blockedOrigin.current && !blocked;
      },
      onDragStart: (event: DragEvent<HTMLDivElement>) => {
        if (
          blocked ||
          saving.current ||
          blockedOrigin.current ||
          !workflowMoves(item, permissions).length ||
          (event.target as Element).closest("[data-no-drag]")
        ) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", item.id);
        active.current = item;
        setDragged(item);
        setContinuation(null);
      },
      onDragEnd: cancel,
      onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => {
        if (Date.now() < suppressClickUntil.current) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
    }),
    columnProps: (stage: WorkflowPipelineStage) => {
      const destination = dragged
        ? workflowMoves(dragged, permissions).find(
            (value) => value.stage === stage,
          )
        : undefined;
      return {
        "data-drop-state": dragged
          ? over === stage && destination
            ? "over"
            : destination
              ? "allowed"
              : "blocked"
          : undefined,
        onDragOver: (event: DragEvent<HTMLElement>) => {
          if (!active.current) return;
          const allowed = workflowMoves(active.current, permissions).some(
            (value) => value.stage === stage,
          );
          event.dataTransfer.dropEffect = allowed ? "move" : "none";
          if (allowed) event.preventDefault();
          setOver(stage);
        },
        onDrop: (event: DragEvent<HTMLElement>) => {
          if (!active.current) return;
          event.preventDefault();
          const item = active.current;
          cancel();
          void move(item, stage, null);
        },
      };
    },
  };
}
